import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import { Button } from '@/components/ui/button'
import LinkButton from '@/components/ui/link-button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useMediaQuery } from '@/hooks/use-mobile'
import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import Card from 'app/frontend/base/components/Card'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { getClass } from 'app/frontend/base/services/characterValues'
import { v4 as uuid } from 'uuid'
import {
  getAssignmentScopes,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentViewMode,
  getEffectiveExpectedEntities,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from './assignmentTimeline'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import SpellIcon from './SpellIcon'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import TeamPlanAssignmentRow from './TeamPlanAssignmentRow'
import {
  AssignmentPaletteEntity,
  buildAssignmentPaletteEntities,
} from './assignmentPaletteEntities'
import { CUSTOM_ABILITY_SPELL_ID } from '../utils/encounterAssignmentRows'
import AssignmentEntityChip from './AssignmentEntityChip'
import AssignmentEntitySidebar from './AssignmentEntitySidebar'
import {
  ASSIGNMENT_SIDEBAR_BREAKPOINT,
  FocusedAssignmentScope,
  getAbilityRowDragId,
  getAssignedEntityId,
  parseAbilityRowDragId,
  parseAssignedChipId,
  parseAssignmentDropLocation,
  parseGroupDroppableId,
  parsePaletteDragId,
} from './assignmentDnd'
import {
  applyScopeGroupEntities,
  canAddEntityToScope,
  getScopeEntityIdentities,
  getScopeGroupEntities,
} from './assignmentScopeMutations'
import { getAssignmentUsageCountByIdentity } from './assignmentUsageCounts'
import { EncounterAssignmentRow } from '../utils/encounterAssignmentRows'

const isStandardAbilityKey = (abilityKey: string) => /^\d+$/.test(abilityKey)

const collisionDetection: CollisionDetection = (args) => {
  const activeId = String(args.active.id)
  if (parseAbilityRowDragId(activeId)) {
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((container) =>
        Boolean(parseAbilityRowDragId(String(container.id))),
      ),
    })
  }
  const isPaletteDrag = Boolean(parsePaletteDragId(activeId))
  return pointerWithin({
    ...args,
    droppableContainers: args.droppableContainers.filter((container) => {
      const id = String(container.id)
      if (isPaletteDrag) return Boolean(parseGroupDroppableId(id))
      return Boolean(parseAssignmentDropLocation(id))
    }),
  })
}

const TeamPlanAssignments = () => {
  const { t } = useTranslation()
  const {
    timeline,
    computed,
    canEdit,
    owner,
    basePath,
    raidPlan,
    rosterMemberships,
    updateTimeline,
    setAssignmentDialogOpen,
  } = useTeamPlanTimeline()
  const [openRaidPlanAbilityKey, setOpenRaidPlanAbilityKey] = useState<
    string | null
  >(null)
  const [confirmRemoveAbilityKey, setConfirmRemoveAbilityKey] = useState<
    string | null
  >(null)
  const [focusedScope, setFocusedScope] =
    useState<FocusedAssignmentScope | null>(null)
  const [popupScope, setPopupScope] = useState<{
    abilityKey: string
    scopeId: string
  } | null>(null)
  const [activeDrag, setActiveDrag] = useState<
    | { type: 'palette'; entity: RaidPlanAssignmentEntity }
    | {
        type: 'assigned'
        entity: RaidPlanAssignmentEntity
        index: number
      }
    | null
  >(null)
  const isLargeScreen = useMediaQuery(
    `(min-width: ${ASSIGNMENT_SIDEBAR_BREAKPOINT}px)`,
  )
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
  )

  const encounterRowBySpellId = useMemo(
    () =>
      computed.encounterAbilityRows.reduce<
        Record<number, typeof computed.encounterAbilityRows[number]>
      >((acc, row) => {
        acc[row.spellId] = row
        return acc
      }, {}),
    [computed.encounterAbilityRows],
  )

  const selectedSpellIds = useMemo(() => {
    return Object.entries(timeline.encounter_assignments?.abilities ?? {})
      .filter(([abilityKey]) => isStandardAbilityKey(abilityKey))
      .map(([, ability]) => ability.spell_id)
      .filter((spellId) => Boolean(encounterRowBySpellId[spellId]))
  }, [encounterRowBySpellId, timeline])

  const selectedRows = useMemo(() => {
    const filteredRows = computed.encounterAssignmentRows.filter((row) => {
      if (!isStandardAbilityKey(row.abilityKey)) return true
      if (!encounterRowBySpellId[row.spellId]) return true
      return selectedSpellIds.includes(row.spellId)
    })
    const abilityOrder = timeline.encounter_assignments?.ability_order ?? []
    const orderIndexByAbilityKey = abilityOrder.reduce<Record<string, number>>(
      (acc, abilityKey, index) => {
        acc[abilityKey] = index
        return acc
      },
      {},
    )
    return filteredRows.sort((left, right) => {
      const leftOrder = orderIndexByAbilityKey[left.abilityKey]
      const rightOrder = orderIndexByAbilityKey[right.abilityKey]
      if (leftOrder !== undefined && rightOrder !== undefined) {
        return leftOrder - rightOrder
      }
      if (leftOrder !== undefined) return -1
      if (rightOrder !== undefined) return 1
      return left.first - right.first
    })
  }, [
    computed.encounterAssignmentRows,
    encounterRowBySpellId,
    selectedSpellIds,
    timeline.encounter_assignments?.ability_order,
  ])

  const showSidebar = canEdit && isLargeScreen && selectedRows.length > 0

  const abilityOptions = useMemo(
    () =>
      computed.encounterAbilityRows
        .filter((row) => !selectedSpellIds.includes(row.spellId))
        .map((row) => ({
          spellId: row.spellId,
          label: row.name,
          color: row.color,
        })),
    [computed.encounterAbilityRows, selectedSpellIds],
  )

  const specializationById = useMemo(
    () =>
      computed.specializations.reduce<
        Record<
          number,
          {
            name: string
            icon: string
            classId: number
            className: string
          }
        >
      >((acc, specialization) => {
        acc[specialization.id] = {
          name: specialization.name,
          icon: specialization.icon,
          classId: specialization.classId,
          className: specialization.className,
        }
        return acc
      }, {}),
    [computed.specializations],
  )

  const memberInfoByCharacterId = useMemo(
    () =>
      rosterMemberships.reduce<
        Record<number, { name: string; className: string }>
      >((acc, membership) => {
        const character = membership.characterReference
        acc[character.id] = {
          name: character.name,
          className: character.classId
            ? getClass(character.classId.toString()) ?? 'Unknown'
            : 'Unknown',
        }
        return acc
      }, {}),
    [rosterMemberships],
  )

  const paletteEntities = useMemo<AssignmentPaletteEntity[]>(
    () =>
      buildAssignmentPaletteEntities({
        t,
        members: rosterMemberships,
        specializationById,
        classColor: computed.classColor,
      }),
    [computed.classColor, rosterMemberships, specializationById, t],
  )
  const paletteLabelByIdentity = useMemo(
    () =>
      new Map(
        paletteEntities.map((entry) => [
          getAssignmentEntityIdentity(entry.entity),
          entry.label,
        ]),
      ),
    [paletteEntities],
  )
  const paletteEntityById = useMemo(
    () => new Map(paletteEntities.map((entry) => [entry.id, entry])),
    [paletteEntities],
  )

  useEffect(() => {
    if (showSidebar) return
    setFocusedScope(null)
  }, [showSidebar])

  useEffect(() => {
    if (!focusedScope) return
    const row = selectedRows.find(
      (entry) => entry.abilityKey === focusedScope.abilityKey,
    )
    if (!row) {
      setFocusedScope(null)
      return
    }
    const storedAbility =
      timeline.encounter_assignments?.abilities?.[row.abilityKey]
    const scopes = getAssignmentScopes(
      row.entries,
      getEffectiveAssignmentViewMode(storedAbility?.view_mode),
      t,
      getEffectiveAbilityCustomGrouping(
        storedAbility?.spell_id ?? row.spellId,
        storedAbility?.custom_grouping,
      ),
    )
    if (!scopes.some((scope) => scope.id === focusedScope.scopeId)) {
      setFocusedScope(null)
    }
  }, [focusedScope, selectedRows, t, timeline.encounter_assignments?.abilities])

  useEffect(() => {
    if (!focusedScope) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (popupScope) return
      if (event.defaultPrevented) return
      if (document.querySelector('[role="dialog"]')) return
      event.preventDefault()
      setFocusedScope(null)
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [focusedScope, popupScope])

  const ensureAbilityOnTimeline = (
    abilitiesBySpellId: Record<string, RaidPlanEncounterAssignmentAbility>,
    abilityOrder: RaidPlanEncounterAssignmentAbilityKey[],
    abilityKey: RaidPlanEncounterAssignmentAbilityKey,
    spellId: number,
    castCount: number,
    assignmentGroups: number,
  ) => {
    const existingAbility = abilitiesBySpellId[abilityKey]
    if (existingAbility) {
      abilitiesBySpellId[abilityKey] = {
        ...existingAbility,
        casts: Array.from({ length: castCount }, (_, castIndex) =>
          normalizeAssignmentCast(
            existingAbility.casts?.[castIndex],
            assignmentGroups,
          ),
        ),
      }
      return abilitiesBySpellId[abilityKey]
    }
    const created = {
      spell_id: spellId,
      casts: Array.from({ length: castCount }, () =>
        normalizeAssignmentCast(undefined, assignmentGroups),
      ),
      assignment_groups: undefined,
      message: undefined,
      tts_message: undefined,
      cast_overrides: undefined,
      rgb: undefined,
      raid_plan_link: undefined,
      view_mode: undefined,
      custom_grouping: getEffectiveAbilityCustomGrouping(spellId),
      title: undefined,
    }
    abilitiesBySpellId[abilityKey] = created
    abilityOrder.push(abilityKey)
    return created
  }

  const resolveMutableScope = (
    next: RaidPlanTimeline,
    row: EncounterAssignmentRow,
    scopeId: string,
  ) => {
    if (!next.encounter_assignments) {
      next.encounter_assignments = { abilities: {}, ability_order: [] }
    }
    next.encounter_assignments.abilities =
      next.encounter_assignments.abilities ?? {}
    next.encounter_assignments.ability_order =
      next.encounter_assignments.ability_order ?? []
    const storedAbility = next.encounter_assignments.abilities[row.abilityKey]
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      storedAbility?.assignment_groups,
    )
    const ability = ensureAbilityOnTimeline(
      next.encounter_assignments.abilities,
      next.encounter_assignments.ability_order,
      row.abilityKey,
      row.spellId,
      row.entries.length,
      assignmentGroups,
    )
    const scopes = getAssignmentScopes(
      row.entries,
      getEffectiveAssignmentViewMode(ability.view_mode),
      t,
      getEffectiveAbilityCustomGrouping(
        ability.spell_id,
        ability.custom_grouping,
      ),
    )
    const scope = scopes.find((entry) => entry.id === scopeId)
    if (!scope) return null
    return { ability, scope, assignmentGroups }
  }

  const updateAbility = (
    abilityKey: RaidPlanEncounterAssignmentAbilityKey,
    spellId: number,
    castCount: number,
    assignmentGroups: number,
    updater: (ability: RaidPlanEncounterAssignmentAbility) => void,
    pushHistory = true,
  ) => {
    updateTimeline((next) => {
      if (!next.encounter_assignments) {
        next.encounter_assignments = { abilities: {}, ability_order: [] }
      }
      next.encounter_assignments.abilities =
        next.encounter_assignments.abilities ?? {}
      next.encounter_assignments.ability_order =
        next.encounter_assignments.ability_order ?? []
      const ability = ensureAbilityOnTimeline(
        next.encounter_assignments.abilities,
        next.encounter_assignments.ability_order,
        abilityKey,
        spellId,
        castCount,
        assignmentGroups,
      )
      updater(ability)
    }, pushHistory)
  }

  const addAbility = (spellId: number) => {
    const row = encounterRowBySpellId[spellId]
    if (!row) return
    const abilityKey = spellId.toString()
    updateAbility(
      abilityKey,
      spellId,
      row.entries.length,
      row.assignmentGroups,
      () => null,
    )
  }

  const addCustomAbility = () => {
    const abilityKey = uuid().replaceAll('-', '_')
    updateAbility(abilityKey, CUSTOM_ABILITY_SPELL_ID, 0, 1, () => null)
    setOpenRaidPlanAbilityKey(abilityKey)
  }

  const removeAbility = (abilityKey: RaidPlanEncounterAssignmentAbilityKey) => {
    updateTimeline((next) => {
      if (!next.encounter_assignments?.abilities) return
      delete next.encounter_assignments.abilities[abilityKey]
      next.encounter_assignments.ability_order =
        next.encounter_assignments.ability_order?.filter(
          (entryKey) => entryKey !== abilityKey,
        ) ?? []
    }, true)
    if (focusedScope?.abilityKey === abilityKey) setFocusedScope(null)
    if (popupScope?.abilityKey === abilityKey) setPopupScope(null)
  }

  const addEntityToScope = (
    location: FocusedAssignmentScope,
    entity: RaidPlanAssignmentEntity,
    options?: { toggleIfPresent?: boolean },
  ) => {
    const row = selectedRows.find(
      (entry) => entry.abilityKey === location.abilityKey,
    )
    if (!row) return
    updateTimeline((next) => {
      const resolved = resolveMutableScope(next, row, location.scopeId)
      if (!resolved) return
      const { ability, scope, assignmentGroups } = resolved
      const scopeIdentities = getScopeEntityIdentities(
        ability,
        scope,
        assignmentGroups,
      )
      const identity = getAssignmentEntityIdentity(entity)
      if (!canAddEntityToScope(entity, scopeIdentities)) {
        if (!options?.toggleIfPresent) return
        scope.castIndexes.forEach((castIndex) => {
          const cast = normalizeAssignmentCast(
            ability.casts[castIndex],
            assignmentGroups,
          )
          cast.boxes = cast.boxes.map((box) => ({
            ...box,
            entities: box.entities.filter(
              (boxEntity) =>
                getAssignmentEntityIdentity(boxEntity) !== identity,
            ),
          }))
          ability.casts[castIndex] = cast
        })
        return
      }
      const currentEntities = getScopeGroupEntities(
        ability,
        scope,
        assignmentGroups,
        location.groupIndex,
      )
      applyScopeGroupEntities(
        ability,
        scope,
        assignmentGroups,
        location.groupIndex,
        [...currentEntities, { ...entity, id: uuid() }],
      )
    }, true)
  }

  const moveAssignmentEntity = (
    source: FocusedAssignmentScope & { entityId: string },
    target: FocusedAssignmentScope,
  ) => {
    const sourceRow = selectedRows.find(
      (row) => row.abilityKey === source.abilityKey,
    )
    const targetRow = selectedRows.find(
      (row) => row.abilityKey === target.abilityKey,
    )
    if (!sourceRow || !targetRow) return
    const isSameGroup =
      source.abilityKey === target.abilityKey &&
      source.scopeId === target.scopeId &&
      source.groupIndex === target.groupIndex
    if (isSameGroup) return
    updateTimeline((next) => {
      const sourceResolved = resolveMutableScope(
        next,
        sourceRow,
        source.scopeId,
      )
      const targetResolved = resolveMutableScope(
        next,
        targetRow,
        target.scopeId,
      )
      if (!sourceResolved || !targetResolved) return
      if (source.abilityKey === target.abilityKey) {
        sourceResolved.ability = targetResolved.ability
        sourceResolved.assignmentGroups = targetResolved.assignmentGroups
      }
      const sourceEntities = getScopeGroupEntities(
        sourceResolved.ability,
        sourceResolved.scope,
        sourceResolved.assignmentGroups,
        source.groupIndex,
      )
      const entity = sourceEntities.find(
        (item, index) => getAssignedEntityId(item, index) === source.entityId,
      )
      if (!entity) return
      const isSameScope =
        source.abilityKey === target.abilityKey &&
        source.scopeId === target.scopeId
      const targetIdentities = getScopeEntityIdentities(
        targetResolved.ability,
        targetResolved.scope,
        targetResolved.assignmentGroups,
      )
      if (!isSameScope && !canAddEntityToScope(entity, targetIdentities)) {
        return
      }
      applyScopeGroupEntities(
        sourceResolved.ability,
        sourceResolved.scope,
        sourceResolved.assignmentGroups,
        source.groupIndex,
        sourceEntities.filter(
          (item, index) => getAssignedEntityId(item, index) !== source.entityId,
        ),
      )
      const targetEntities = getScopeGroupEntities(
        targetResolved.ability,
        targetResolved.scope,
        targetResolved.assignmentGroups,
        target.groupIndex,
      )
      applyScopeGroupEntities(
        targetResolved.ability,
        targetResolved.scope,
        targetResolved.assignmentGroups,
        target.groupIndex,
        [...targetEntities, entity],
      )
    }, true)
  }

  const reorderAssignmentEntity = (
    location: FocusedAssignmentScope,
    entityId: string,
    overEntityId: string,
  ) => {
    const row = selectedRows.find(
      (entry) => entry.abilityKey === location.abilityKey,
    )
    if (!row) return
    updateTimeline((next) => {
      const resolved = resolveMutableScope(next, row, location.scopeId)
      if (!resolved) return
      const currentEntities = getScopeGroupEntities(
        resolved.ability,
        resolved.scope,
        resolved.assignmentGroups,
        location.groupIndex,
      )
      const sourceIndex = currentEntities.findIndex(
        (item, index) => getAssignedEntityId(item, index) === entityId,
      )
      const targetIndex = currentEntities.findIndex(
        (item, index) => getAssignedEntityId(item, index) === overEntityId,
      )
      if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
        return
      }
      applyScopeGroupEntities(
        resolved.ability,
        resolved.scope,
        resolved.assignmentGroups,
        location.groupIndex,
        arrayMove(currentEntities, sourceIndex, targetIndex),
      )
    }, true)
  }

  const handleAbilityDragEnd = (activeId: string, overId: string) => {
    const sourceKey = parseAbilityRowDragId(activeId)
    const targetKey = parseAbilityRowDragId(overId)
    if (!sourceKey || !targetKey) return
    const sourceIndex = selectedRows.findIndex(
      (row) => row.abilityKey === sourceKey,
    )
    const targetIndex = selectedRows.findIndex(
      (row) => row.abilityKey === targetKey,
    )
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
      return
    }
    const reorderedAbilityKeys = arrayMove(
      selectedRows.map((row) => row.abilityKey),
      sourceIndex,
      targetIndex,
    )
    updateTimeline((next) => {
      if (!next.encounter_assignments) {
        next.encounter_assignments = { abilities: {}, ability_order: [] }
      }
      const existingOrder = next.encounter_assignments.ability_order ?? []
      const remainingOrder = existingOrder.filter(
        (abilityKey) => !reorderedAbilityKeys.includes(abilityKey),
      )
      next.encounter_assignments.ability_order = [
        ...reorderedAbilityKeys,
        ...remainingOrder,
      ]
    }, true)
  }

  const handleDragStart = (event: DragStartEvent) => {
    if (!canEdit) return
    const activeId = String(event.active.id)
    const paletteId = parsePaletteDragId(activeId)
    if (paletteId) {
      const entry = paletteEntityById.get(paletteId)
      if (!entry) return
      setActiveDrag({ type: 'palette', entity: entry.entity })
      return
    }
    const assigned = parseAssignedChipId(activeId)
    if (!assigned) return
    const row = selectedRows.find(
      (entry) => entry.abilityKey === assigned.abilityKey,
    )
    if (!row) return
    const storedAbility =
      timeline.encounter_assignments?.abilities?.[row.abilityKey]
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      storedAbility?.assignment_groups,
    )
    const scopes = getAssignmentScopes(
      row.entries,
      getEffectiveAssignmentViewMode(storedAbility?.view_mode),
      t,
      getEffectiveAbilityCustomGrouping(
        storedAbility?.spell_id ?? row.spellId,
        storedAbility?.custom_grouping,
      ),
    )
    const scope = scopes.find((entry) => entry.id === assigned.scopeId)
    if (!scope || !storedAbility) return
    const entities = getScopeGroupEntities(
      storedAbility,
      scope,
      assignmentGroups,
      assigned.groupIndex,
    )
    const entityIndex = entities.findIndex(
      (item, index) => getAssignedEntityId(item, index) === assigned.entityId,
    )
    const entity = entities[entityIndex]
    if (!entity) return
    const displayIndex = Array.from(
      { length: assigned.groupIndex },
      (_, groupIndex) =>
        getScopeGroupEntities(
          storedAbility,
          scope,
          assignmentGroups,
          groupIndex,
        ).length,
    ).reduce((sum, count) => sum + count, entityIndex)
    setActiveDrag({ type: 'assigned', entity, index: displayIndex })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null)
    if (!canEdit) return
    const activeId = String(event.active.id)
    const overId = event.over?.id ? String(event.over.id) : ''
    if (!overId) return
    if (parseAbilityRowDragId(activeId)) {
      handleAbilityDragEnd(activeId, overId)
      return
    }
    const dropLocation = parseAssignmentDropLocation(overId)
    if (!dropLocation) return
    const paletteId = parsePaletteDragId(activeId)
    if (paletteId) {
      const entry = paletteEntityById.get(paletteId)
      if (!entry) return
      addEntityToScope(dropLocation, entry.entity)
      setFocusedScope(dropLocation)
      return
    }
    const assigned = parseAssignedChipId(activeId)
    if (!assigned) return
    const overAssigned = parseAssignedChipId(overId)
    const isSameGroup =
      assigned.abilityKey === dropLocation.abilityKey &&
      assigned.scopeId === dropLocation.scopeId &&
      assigned.groupIndex === dropLocation.groupIndex
    if (isSameGroup) {
      if (!overAssigned || overAssigned.entityId === assigned.entityId) return
      reorderAssignmentEntity(
        assigned,
        assigned.entityId,
        overAssigned.entityId,
      )
      return
    }
    moveAssignmentEntity(assigned, dropLocation)
    setFocusedScope(dropLocation)
  }

  const focusedRow = focusedScope
    ? selectedRows.find((row) => row.abilityKey === focusedScope.abilityKey)
    : undefined
  const focusedStoredAbility = focusedRow
    ? timeline.encounter_assignments?.abilities?.[focusedRow.abilityKey]
    : undefined
  const focusedAssignmentGroups = focusedRow
    ? getEffectiveAssignmentGroups(
        focusedRow.assignmentGroups,
        focusedStoredAbility?.assignment_groups,
      )
    : 1
  const focusedScopes = focusedRow
    ? getAssignmentScopes(
        focusedRow.entries,
        getEffectiveAssignmentViewMode(focusedStoredAbility?.view_mode),
        t,
        getEffectiveAbilityCustomGrouping(
          focusedStoredAbility?.spell_id ?? focusedRow.spellId,
          focusedStoredAbility?.custom_grouping,
        ),
      )
    : []
  const focusedScopeEntry = focusedScopes.find(
    (scope) => scope.id === focusedScope?.scopeId,
  )
  const focusedUsageCountByIdentity =
    focusedRow && focusedStoredAbility
      ? getAssignmentUsageCountByIdentity({
          ability: {
            ...focusedStoredAbility,
            casts: Array.from(
              { length: focusedRow.entries.length },
              (_, castIndex) =>
                normalizeAssignmentCast(
                  focusedStoredAbility.casts?.[castIndex],
                  focusedAssignmentGroups,
                ),
            ),
          },
          scopes: focusedScopes,
          assignmentGroups: focusedAssignmentGroups,
        })
      : {}
  const focusedTargetLabel =
    focusedRow && focusedScopeEntry && focusedScope
      ? focusedAssignmentGroups > 1
        ? `${focusedStoredAbility?.title ?? focusedRow.name} (${
            focusedScopeEntry.label
          }, ${t('dashboard.team.plans.assignments.group-label', {
            count: focusedScope.groupIndex + 1,
          })})`
        : `${focusedStoredAbility?.title ?? focusedRow.name} (${
            focusedScopeEntry.label
          })`
      : null
  const focusedScopeIdentities =
    focusedRow && focusedStoredAbility && focusedScopeEntry
      ? getScopeEntityIdentities(
          {
            ...focusedStoredAbility,
            casts: Array.from(
              { length: focusedRow.entries.length },
              (_, castIndex) =>
                normalizeAssignmentCast(
                  focusedStoredAbility.casts?.[castIndex],
                  focusedAssignmentGroups,
                ),
            ),
          },
          focusedScopeEntry,
          focusedAssignmentGroups,
        )
      : new Set<string>()
  const focusedNormalizedAbility =
    focusedRow && focusedStoredAbility
      ? {
          ...focusedStoredAbility,
          casts: Array.from(
            { length: focusedRow.entries.length },
            (_, castIndex) =>
              normalizeAssignmentCast(
                focusedStoredAbility.casts?.[castIndex],
                focusedAssignmentGroups,
              ),
          ),
        }
      : undefined
  const focusedGroupEntities =
    focusedNormalizedAbility && focusedScopeEntry && focusedScope
      ? getScopeGroupEntities(
          focusedNormalizedAbility,
          focusedScopeEntry,
          focusedAssignmentGroups,
          focusedScope.groupIndex,
        )
      : []
  const focusedExpectedCount =
    focusedNormalizedAbility && focusedScopeEntry && focusedScope
      ? getEffectiveExpectedEntities(
          focusedStoredAbility?.expected_entities,
          normalizeAssignmentBox(
            focusedNormalizedAbility.casts[focusedScopeEntry.castIndexes[0]]
              ?.boxes[focusedScope.groupIndex],
          ).expected_entities,
        )
      : undefined
  const addAbilityMenu = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button type='button' variant='outline'>
          {t('dashboard.team.plans.assignments.add-ability')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {abilityOptions.map((option) => (
          <DropdownMenuItem
            key={option.spellId}
            onClick={() => addAbility(option.spellId)}
          >
            <div className='flex items-center gap-2 min-w-0'>
              <SpellIcon
                spellId={option.spellId}
                className='w-4 h-4 rounded border border-border shrink-0'
              />
              <span className='truncate' style={{ color: option.color }}>
                {option.label}
              </span>
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={addCustomAbility}>
          <div className='flex items-center gap-2 min-w-0'>
            <SpellIcon
              spellId={CUSTOM_ABILITY_SPELL_ID}
              className='w-4 h-4 rounded border border-border shrink-0'
            />
            <span className='truncate'>
              {t('dashboard.team.plans.assignments.custom')}
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveDrag(null)}
    >
      <div className='flex items-start gap-4 mt-4'>
        <div className='grow min-w-0 space-y-3'>
          {selectedRows.length === 0 ? (
            <div className='py-8 text-center'>
              <div className='text-sm text-muted-foreground'>
                {t('dashboard.team.plans.assignments.empty')}
              </div>
              <div className='mt-3 flex justify-center gap-2'>
                {canEdit ? addAbilityMenu : null}
                <LinkButton to='/support/events/raid-plan-assignments'>
                  {t('dashboard.team.plans.assignments.sidebar-help')}
                </LinkButton>
              </div>
            </div>
          ) : null}

          <SortableContext
            items={selectedRows.map((row) =>
              getAbilityRowDragId(row.abilityKey),
            )}
            strategy={verticalListSortingStrategy}
          >
            {selectedRows.map((row) => (
              <TeamPlanAssignmentRow
                key={row.abilityKey}
                row={row}
                t={t}
                canEdit={canEdit}
                encounterId={computed.encounter?.id}
                difficulty={raidPlan.difficulty}
                owner={owner}
                basePath={basePath}
                openRaidPlanAbilityKey={openRaidPlanAbilityKey}
                setOpenRaidPlanAbilityKey={setOpenRaidPlanAbilityKey}
                setConfirmRemoveAbilityKey={setConfirmRemoveAbilityKey}
                setAssignmentDialogOpen={setAssignmentDialogOpen}
                focusedScope={focusedScope}
                onFocusScope={setFocusedScope}
                popupScope={popupScope}
                onPopupScopeChange={setPopupScope}
                showSidebar={showSidebar}
                paletteEntities={paletteEntities}
                paletteLabelByIdentity={paletteLabelByIdentity}
                memberInfoByCharacterId={memberInfoByCharacterId}
                specializationById={specializationById}
                storedAbility={
                  timeline.encounter_assignments?.abilities?.[row.abilityKey]
                }
                updateAbility={updateAbility}
                onCommit={() => updateTimeline(() => null, true)}
                removeAbility={removeAbility}
                phaseDividers={computed.phaseDividers}
              />
            ))}
          </SortableContext>

          {canEdit && selectedRows.length > 0 ? (
            <div className='flex justify-end'>{addAbilityMenu}</div>
          ) : null}
          <ConfirmDialog
            open={Boolean(confirmRemoveAbilityKey)}
            descriptionKey='dashboard.team.plans.assignments.remove-ability-confirm'
            onCancel={() => setConfirmRemoveAbilityKey(null)}
            onContinue={() => {
              if (!confirmRemoveAbilityKey) return
              removeAbility(confirmRemoveAbilityKey)
              setConfirmRemoveAbilityKey(null)
            }}
            variant='destructive'
          />
        </div>
        {showSidebar ? (
          <AssignmentEntitySidebar
            paletteEntities={paletteEntities}
            assignmentUsageCountByIdentity={focusedUsageCountByIdentity}
            focusedScopeIdentities={focusedScopeIdentities}
            focusedTargetLabel={focusedTargetLabel}
            focusedAssignedCount={focusedGroupEntities.length}
            focusedExpectedCount={focusedExpectedCount}
            focusedSpellId={focusedRow?.spellId}
            canEdit={canEdit}
            onAddEntity={(entity) => {
              if (!focusedScope) return
              addEntityToScope(focusedScope, entity, { toggleIfPresent: true })
            }}
            onOpenPopup={() => {
              if (!focusedScope) return
              setPopupScope({
                abilityKey: focusedScope.abilityKey,
                scopeId: focusedScope.scopeId,
              })
              setAssignmentDialogOpen(true)
            }}
          />
        ) : null}
      </div>
      {typeof document !== 'undefined'
        ? createPortal(
            <DragOverlay dropAnimation={null}>
              {activeDrag ? (
                activeDrag.type === 'assigned' ? (
                  <AssignmentEntityChip
                    index={activeDrag.index}
                    label={
                      paletteLabelByIdentity.get(
                        getAssignmentEntityIdentity(activeDrag.entity),
                      ) ?? activeDrag.entity.kind
                    }
                  />
                ) : (
                  <Card className='inline-flex items-center text-sm p-0'>
                    <span className='inline-flex min-w-0 items-center px-2 py-1'>
                      {paletteLabelByIdentity.get(
                        getAssignmentEntityIdentity(activeDrag.entity),
                      ) ?? activeDrag.entity.kind}
                    </span>
                  </Card>
                )
              ) : null}
            </DragOverlay>,
            document.body,
          )
        : null}
    </DndContext>
  )
}

export default TeamPlanAssignments
